/**
 * RoleGuard — TanStack Router `beforeLoad` guard for role-based access control.
 *
 * Creates a guard function that checks the authenticated user's role against
 * a list of allowed roles. Redirects unauthenticated users to /login.
 * Redirects unauthorized (wrong role) users to /403.
 */

import { redirect } from '@tanstack/react-router';

import { useAuthStore } from '../stores/auth-store';
import type { PortalUserRole } from '../types/portal-role';

/**
 * Creates a `beforeLoad` guard for TanStack Router that enforces role-based access.
 *
 * @param allowedRoles - Array of roles permitted to access this route
 * @returns A `beforeLoad` function compatible with TanStack Router
 */
export function createRoleGuard(allowedRoles: PortalUserRole[]) {
  return () => {
    const { role, isAuthenticated } = useAuthStore.getState();

    if (!isAuthenticated || !role) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack redirect() is the idiomatic control-flow throw
      throw redirect({ to: '/login' });
    }

    if (!allowedRoles.includes(role)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack redirect() is the idiomatic control-flow throw
      throw redirect({ to: '/403' });
    }
  };
}
